import React from 'react';
import {
  AppLayout,
  Button,
  ColumnLayout,
  Header,
  SpaceBetween,
  Container,
  Link,
  HelpPanel,
  Box,
} from '@cloudscape-design/components';
import ServiceNavigation from '../ServiceNavigation.jsx';
import Breadcrumbs from '../Breadcrumbs.jsx';
import ExtendingPanel from '../ExtendingPanel.jsx';
import SynopsisPanel from '../SynopsisPanel.jsx';
import BrowserCheck from '../BrowserCheck';
import { sendAnalytics } from '../../resources/rum-provider';
import { EDGE_FUNCTIONS_CF_ENDPOINT } from '../../resources/prod-env.jsx';
import '../../styles/form.scss';

const demoModule = "ab-testing";
const AB_TEST_URL = EDGE_FUNCTIONS_CF_ENDPOINT + '/ab-test/index.html';

export default class AbTesting extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toolsIndex: 0,
      toolsOpen: false,
      iframeKey: 0,
      currentVariant: null,
    };
  }

  replaceToolsContent = (index) => {
    this.setState({ toolsIndex: index, toolsOpen: true });
  };

  handleReload = () => {
    sendAnalytics({ demo: demoModule });
    this.setState(prev => ({ iframeKey: prev.iframeKey + 1, currentVariant: null }));
  };

  onIframeLoad = (e) => {
    try {
      const doc = e.target.contentDocument || e.target.contentWindow.document;
      const text = doc.body.innerText || '';
      if (text.includes('Variant A')) this.setState({ currentVariant: 'A' });
      else if (text.includes('Variant B')) this.setState({ currentVariant: 'B' });
    } catch (err) { /* cross-origin */ }
  };

  render() {
    return (
      <AppLayout
        navigation={<ServiceNavigation />}
        breadcrumbs={<Breadcrumbs items={BreadcrumbsItems} />}
        content={
          <Content
            replaceToolsContent={this.replaceToolsContent}
            handleReload={this.handleReload}
            onIframeLoad={this.onIframeLoad}
            state={this.state}
          />
        }
        tools={Tools[this.state.toolsIndex]}
        toolsOpen={this.state.toolsOpen}
        onToolsChange={({ detail }) => this.setState({ toolsOpen: detail.open })}
        contentType="default"
      />
    );
  }
}

const Content = ({ replaceToolsContent, handleReload, onIframeLoad, state }) => {
  const v = state.currentVariant;
  const variantInfo = v === 'A'
    ? { label: '🅰️ Variant A', header: 'X-Ab-Variant: variant-a', color: '#FF9900', bg: 'rgba(255,153,0,0.08)', border: '#FF9900' }
    : v === 'B'
    ? { label: '🅱️ Variant B', header: 'X-Ab-Variant: variant-b', color: '#146eb4', bg: 'rgba(20,110,180,0.08)', border: '#146eb4' }
    : null;
  return (
    <React.Fragment>
      <BrowserCheck />
      <SpaceBetween direction="vertical" size="l">
        <SynopsisPanel videolink="https://broadcast.amazon.com/videos/2001129">
          <SpaceBetween direction="vertical" size="s">
            <Box>
              <Box variant="strong">CloudFront Function &lt; 1ms</Box> — A CloudFront Function at viewer-request
              routes each request to variant A or variant B using random selection. The routing decision happens entirely at the edge in under 1 ms.
              Each request has a 50/50 chance of being routed to either variant.
            </Box>
            <Box variant="strong">Architecture</Box>
            <div style={{ background: '#f8f8f8', border: '1px solid #d5dbdb', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', minWidth: 650 }}>
                <div style={{ textAlign: 'center', minWidth: 70 }}><div style={{ fontSize: '2rem' }}>👤</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>User</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>GET /ab-test/*</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 90 }}><div style={{ fontSize: '2rem' }}>☁️</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>CloudFront</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>Viewer Request</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 110, border: '2px solid #FF9900', borderRadius: 10, padding: '0.5rem', background: 'rgba(255,153,0,0.06)' }}><div style={{ fontSize: '2rem' }}>⚡</div><div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#FF9900' }}>CF Function</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>A/B Router (random)</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ background: 'rgba(20,110,180,0.08)', border: '1px solid #146eb4', borderRadius: 4, padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: '#146eb4', fontWeight: 600 }}>🅰️ /ab-test/variant-a/</div>
                  <div style={{ background: 'rgba(255,153,0,0.08)', border: '1px solid #FF9900', borderRadius: 4, padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: '#FF9900', fontWeight: 600 }}>🅱️ /ab-test/variant-b/</div>
                </div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 70 }}><div style={{ fontSize: '2rem' }}>🪣</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Amazon S3</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>Origin</div></div>
              </div>
            </div>
            <Box variant="strong">CloudFront Function code</Box>
            <div style={{ background: '#0d1117', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <pre style={{ margin: 0, color: '#c9d1d9', fontSize: '0.82rem', lineHeight: 1.6, fontFamily: "'Courier New', monospace" }}>{`function handler(event) {
    var request = event.request;
    var uri = request.uri;

    if (!uri.startsWith('/ab-test')) return request;

    // Random 50/50 split at the edge
    var rand = Math.random() * 100;
    var variant = rand < 50 ? 'variant-a' : 'variant-b';

    // Rewrite URI to serve the assigned variant
    request.uri = '/ab-test/' + variant + '/index.html';

    return request;
}`}</pre>
            </div>
            <ul>
              <li><code>Math.random()</code> generates a value 0–100 on every request at the edge</li>
              <li>If &lt; 50 → rewrites URI to <code>/ab-test/variant-a/</code>, otherwise <code>/ab-test/variant-b/</code></li>
              <li>Each variant is a different S3 origin path — different content served</li>
              <li>A viewer-response CFF adds <code>X-AB-Variant</code> header so you can verify in DevTools</li>
            </ul>
            <Box variant="strong">Comparison</Box>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #d5dbdb', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Approach</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Latency</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Config Change</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Consistency</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '0.4rem 0.6rem' }}>Backend A/B (server-side)</td><td style={{ padding: '0.4rem 0.6rem' }}>50-200ms</td><td style={{ padding: '0.4rem 0.6rem' }}>Requires redeploy</td><td style={{ padding: '0.4rem 0.6rem' }}>Session-based</td></tr>
                <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '0.4rem 0.6rem' }}>DNS-based split</td><td style={{ padding: '0.4rem 0.6rem' }}>TTL dependent</td><td style={{ padding: '0.4rem 0.6rem' }}>DNS propagation delay</td><td style={{ padding: '0.4rem 0.6rem' }}>IP-based</td></tr>
                <tr><td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>CF Function</td><td style={{ padding: '0.4rem 0.6rem' }}>&lt; 1ms</td><td style={{ padding: '0.4rem 0.6rem' }}>Real-time</td><td style={{ padding: '0.4rem 0.6rem' }}>Per-request</td></tr>
              </tbody>
            </table>
          </SpaceBetween>
        </SynopsisPanel>

        <Container header={<Header variant="h2">Test Live — Real request through CloudFront</Header>}>
          <SpaceBetween direction="vertical" size="s">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={handleReload} iconName="refresh">
                Send new request
              </Button>
              {variantInfo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ background: variantInfo.bg, border: '2px solid ' + variantInfo.border, borderRadius: 8, padding: '0.4rem 0.8rem', fontWeight: 700, color: variantInfo.color }}>
                    {variantInfo.label}
                  </div>
                  <Box variant="code">{variantInfo.header}</Box>
                </div>
              )}
            </div>
            <div style={{ border: '2px solid ' + (variantInfo ? variantInfo.border : '#d5dbdb'), borderRadius: 8, overflow: 'hidden', background: '#fff', transition: 'border-color 0.3s' }}>
              <iframe
                key={state.iframeKey}
                src={AB_TEST_URL + '?t=' + Date.now() + '_' + state.iframeKey}
                title="A/B Test Result"
                style={{ width: '100%', height: 340, border: 'none' }}
                onLoad={onIframeLoad}
              />
            </div>
            <Box variant="small" color="text-body-secondary">
              Endpoint: <Box variant="code" display="inline">{AB_TEST_URL}</Box> · Each click sends a fresh request routed by the CFF at the edge.
            </Box>
          </SpaceBetween>
        </Container>

        <ExtendingPanel label="Extend it">
          <ColumnLayout columns={2} variant="text-grid">
            <Box>
              Questions/ideas on this demo? <Box variant="strong">Daniel Martinez, dmrubio@amazon.es</Box>
            </Box>
            <Box>
              <Link external href="https://github.com/aws-samples/ab-testing-at-edge">
                📘 CloudFront Functions documentation
              </Link>
            </Box>
          </ColumnLayout>
        </ExtendingPanel>
      </SpaceBetween>
    </React.Fragment>
  );
};

const BreadcrumbsItems = [
  { text: 'Home', href: '/' },
  { text: 'A/B Testing', href: '/abtesting' },
];

const Tools = [
  <HelpPanel header={<h3>A/B Testing at Edge</h3>}>
    <p>This demo loads content directly from a CloudFront distribution. A CloudFront Function at viewer-request
    rewrites the URI to serve variant A or variant B.</p>
    <p>The variant assignment happens entirely at the edge — no origin logic involved. Click the button to send a new request and see which variant the CFF assigns.</p>
  </HelpPanel>,
];
